/**
 * Answer a signal — the loop closing (canon 23 §4.1 present-tense responses).
 *
 * Hold course / Change course become a Decision on the record, linked to the
 * signal through respond_to_signals, with the pre-registered expectation
 * ("if this holds, we expect Y; we'll know by Z") written into the Decision's
 * reconsideration_conditions + review_by_date. This is what "On the record"
 * renders afterward — a reader who wasn't in the room can act on it.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSignalOccurrence, type SignalEventRow } from '../../types/signal-events';
import { answerSignal } from '../../lib/signalEventsService';
import { GhostButton, PorModal, PrimaryButton } from '../plan-of-record/porUi';

const KIND_COPY = {
  hold: {
    label: 'Hold course',
    hint: 'The signal does not warrant a change — holding is a decision, not inaction.',
    titlePrefix: 'Held course against',
  },
  change: {
    label: 'Change course',
    hint: 'The plan changes because of this signal — the change carries its why.',
    titlePrefix: 'Changed course on',
  },
} as const;

export function AnswerSignalModal({
  row,
  itemOptions,
  initialKind,
  replanCycleId,
  onClose,
  onAnswered,
}: {
  row: SignalEventRow;
  /**
   * Plan commitments the answer can attach to (when the signal itself carries
   * no item). The decision then lands as a terms line on that commitment's row.
   */
  itemOptions?: { id: string; title: string }[];
  /** Which answer the caller led with (e.g. the row's "Hold course" button). */
  initialKind?: 'hold' | 'change';
  /**
   * The plan cycle a change-course answer replans (when the signal itself
   * carries none). With a cycle known, "change course" doesn't dead-end at
   * "recorded" — it carries the decision into the replan act.
   */
  replanCycleId?: string | null;
  onClose: () => void;
  onAnswered: () => Promise<void> | void;
}) {
  const navigate = useNavigate();
  const occurrence = getSignalOccurrence(row.occurrence)?.label ?? row.occurrence;
  const [kind, setKind] = useState<'hold' | 'change'>(initialKind ?? 'hold');
  const [rationale, setRationale] = useState('');
  const [expectation, setExpectation] = useState('');
  const [checkBy, setCheckBy] = useState('');
  const [appliesToItemId, setAppliesToItemId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<{ decisionId: string; cycleId: string } | null>(null);

  const showAppliesTo = !row.item_id && (itemOptions?.length ?? 0) > 0;

  const handleAnswer = async () => {
    if (!rationale.trim()) {
      setError('A rationale is required — it becomes the decision on the record.');
      return;
    }
    setBusy(true);
    setError(null);
    const result = await answerSignal({
      workspaceId: row.workspace_id,
      signalEventId: row.id,
      decisionTitle: `${KIND_COPY[kind].titlePrefix} "${occurrence}"`,
      responseKind: kind,
      rationale,
      expectation: expectation.trim() || undefined,
      checkBy: checkBy || undefined,
      cycleId: row.cycle_id,
      itemId: row.item_id ?? (appliesToItemId || null),
    });
    setBusy(false);
    if (!result.success) {
      setError(result.error ?? 'Could not record the answer.');
      return;
    }
    await onAnswered();
    // Changing course means the plan changes: carry the decision into the
    // replan act instead of dead-ending at "recorded".
    const targetCycleId = row.cycle_id ?? replanCycleId ?? null;
    if (kind === 'change' && result.decisionId && targetCycleId) {
      setRecorded({ decisionId: result.decisionId, cycleId: targetCycleId });
      return;
    }
    onClose();
  };

  if (recorded) {
    return (
      <PorModal
        title="On the record"
        subtitle={occurrence}
        onClose={onClose}
        footer={
          <>
            <GhostButton onClick={onClose}>Later</GhostButton>
            <PrimaryButton
              onClick={() => {
                onClose();
                navigate(
                  `/planning/plans/${recorded.cycleId}/governance?changeCourse=${recorded.decisionId}`
                );
              }}
            >
              Replan now →
            </PrimaryButton>
          </>
        }
      >
        <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          The decision is on the record — but the plan does not change itself. Carry it into a
          replan: the new plan version will name this decision as its why, and the change log
          will link them.
        </p>
      </PorModal>
    );
  }

  const label = 'block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-1.5';
  const field = 'w-full text-sm  border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]';

  return (
    <PorModal
      title="Answer the signal"
      subtitle={occurrence}
      onClose={onClose}
      footer={
        <>
          <GhostButton onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleAnswer} disabled={busy || rationale.trim().length === 0}>
            {busy ? 'Recording…' : `Record — ${KIND_COPY[kind].label.toLowerCase()}`}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <div className="grid grid-cols-2 gap-2">
            {(['hold', 'change'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={` border px-3 py-2 text-sm font-medium transition-colors ${
                  kind === k
                    ? 'border-primary-300 bg-primary-50 text-primary-800 dark:bg-[var(--active-tint)] dark:text-[var(--text-pri)]'
                    : 'border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]'
                }`}
              >
                {KIND_COPY[k].label}
              </button>
            ))}
          </div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1.5">{KIND_COPY[kind].hint}</p>
        </div>

        <div>
          <label className={label}>
            Why <span className="text-luminous-coral-700">*</span>
          </label>
          <textarea
            value={rationale}
            onChange={(e) => {
              setRationale(e.target.value);
              if (error) setError(null);
            }}
            rows={3}
            placeholder="The reasoning — this becomes the decision on the record, readable by someone who wasn't in the room."
            className={`${field} resize-none`}
            autoFocus
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
          <div>
            <label className={label}>
              If this holds, we expect… <span className="normal-case font-normal">(optional)</span>
            </label>
            <input
              value={expectation}
              onChange={(e) => setExpectation(e.target.value)}
              placeholder="The observable outcome you're predicting"
              className={field}
            />
          </div>
          <div>
            <label className={label}>Check by</label>
            <input
              type="date"
              value={checkBy}
              onChange={(e) => setCheckBy(e.target.value)}
              className={field}
            />
          </div>
        </div>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          The expectation and date are pre-registered on the decision — they are what a future
          retrospective judges it against.
        </p>

        {showAppliesTo && (
          <div>
            <label className={label}>
              Applies to <span className="normal-case font-normal">(optional)</span>
            </label>
            <select
              value={appliesToItemId}
              onChange={(e) => setAppliesToItemId(e.target.value)}
              className={field}
            >
              <option value="">Whole plan — no single commitment</option>
              {itemOptions!.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.title}
                </option>
              ))}
            </select>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
              Pick a commitment and the answer lands as a terms line on its row.
            </p>
          </div>
        )}

        {error && <p className="text-xs text-luminous-coral-700">{error}</p>}
      </div>
    </PorModal>
  );
}
